import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Building,
  Building2,
  ClipboardList,
  Download,
  Home,
  LogOut,
  Menu,
  Package,
  Settings,
  ShieldCheck,
  Upload,
  Users,
} from 'lucide-react'
import { useAuth } from '@/context'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import type { ReactNode } from 'react'
import type { Role } from '@/types'

interface NavItem {
  title: string
  href: string
  icon: ReactNode
  roles?: Role[]
}

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/', icon: <Home className="h-5 w-5" /> },
  {
    title: 'Buildings',
    href: '/buildings',
    icon: <Building2 className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Reports',
    href: '/reports',
    icon: <ClipboardList className="h-5 w-5" />,
  },
  {
    title: 'Compliance',
    href: '/compliance',
    icon: <ShieldCheck className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Assets',
    href: '/assets',
    icon: <Package className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Users',
    href: '/users',
    icon: <Users className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Organizations',
    href: '/organizations',
    icon: <Building className="h-5 w-5" />,
    roles: ['super_admin'],
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: <Settings className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Exports',
    href: '/exports',
    icon: <Download className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
  {
    title: 'Imports',
    href: '/imports',
    icon: <Upload className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
  },
]

function NavLink({
  item,
  isActive,
  onClick,
}: {
  item: NavItem
  isActive: boolean
  onClick?: () => void
}) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      {item.icon}
      {item.title}
    </Link>
  )
}

function Sidebar({ userRole, onLinkClick }: { userRole: Role; onLinkClick?: () => void }) {
  const location = useLocation()

  const visibleItems = navItems.filter((item) => !item.roles || item.roles.includes(userRole))

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex h-14 items-center px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <ClipboardList className="h-6 w-6 text-primary" />
          <span>Tidy Tag</span>
        </Link>
      </div>
      <Separator />
      <nav className="flex-1 space-y-1 px-2 py-4">
        {visibleItems.map((item) => (
          <NavLink
            key={item.href}
            item={item}
            isActive={location.pathname === item.href}
            onClick={onLinkClick}
          />
        ))}
      </nav>
    </div>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()

  if (!user) return null

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full">
          <Avatar className="h-9 w-9">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface AppLayoutProps {
  children: ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  if (!user) return null

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card lg:block">
        <Sidebar userRole={user.role} />
      </aside>

      {/* Mobile menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <Sidebar userRole={user.role} onLinkClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-card px-4 lg:px-6">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
          </Sheet>

          <div className="flex-1" />

          <OrgSwitcher />
          <UserMenu />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  )
}
